import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { useEmailVerification, EmailVerificationModal } from '../components/EmailVerification';

const AboutPage = () => {
  // Email verification hook
  const emailVerification = useEmailVerification();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-onyx">
      <Helmet>
        <title>About Us - Luxe Me Japan | Connecting Japanese Brands Globally</title>
        <meta name="description" content="Discover Luxe Me Japan's mission to connect Japanese beauty and lifestyle brands with global markets through authentic storytelling and cultural experiences." />
      </Helmet>
      
      {/* Hero Section */}
      <header className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-gold/10 via-onyx to-onyx"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-3 text-gold tracking-[0.4em] text-[10px] uppercase mb-8 opacity-0 animate-fade-up">
              <span className="w-8 h-px bg-gold/50"></span> About Us
            </div>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl mb-8 opacity-0 animate-fade-up stagger-1 leading-tight">
              Discover <br />
              <span className="text-gradient">Luxe Me Japan</span>
            </h1>
            <p className="text-lg md:text-xl font-light tracking-wide text-platinum/60 max-w-2xl opacity-0 animate-fade-up stagger-2">
              To connect Japanese beauty and lifestyle brands with the global market
              through storytelling and authentic experiences.
            </p>
          </div>
        </div>
      </header>

      {/* Mission Section */}
      <section className="py-24 px-6 lg:px-12 bg-ebony">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative group">
              <div className="absolute -inset-4 border border-gold/20 rounded-2xl group-hover:border-gold/40 transition-all duration-700"></div>
              <img
                src="/images/about-window.jpg"
                alt="Japanese Minimalism"
                className="relative z-10 rounded-xl grayscale group-hover:grayscale-0 transition-all duration-1000 object-cover aspect-[4/5] w-full shadow-2xl"
              />
            </div>
            <div className="space-y-8">
              <div className="inline-flex items-center gap-4 text-gold tracking-[0.4em] text-xs uppercase">
                <span className="w-12 h-px bg-gold"></span> Our Mission
              </div>
              <h2 className="font-serif text-4xl md:text-5xl leading-tight">
                Curating Elegance and <br />
                <span className="text-gold">Cultural Richness</span>
              </h2>
              <p className="text-platinum/60 leading-relaxed text-lg font-light">
                Luxe Me Japan is dedicated to expanding Japanese beauty and lifestyle
                brands into Southeast Asia and Europe through strategic partnerships.
              </p>
              <p className="text-platinum/60 leading-relaxed text-lg font-light">
                We empower clients with strong market presence, leveraging over 20
                years of industry expertise and deep market insights.
              </p>
              <div className="space-y-4 pt-4">
                <div className="flex items-start gap-4">
                  <CheckCircle className="w-5 h-5 text-gold mt-1" />
                  <p className="text-platinum/70">Strategic market entry and expansion guidance</p>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="w-5 h-5 text-gold mt-1" />
                  <p className="text-platinum/70">Authentic cultural storytelling and brand positioning</p>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="w-5 h-5 text-gold mt-1" />
                  <p className="text-platinum/70">Deep connections with key influencers and retailers</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-24 px-6 lg:px-12 bg-onyx">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 space-y-8">
              <div className="inline-flex items-center gap-4 text-gold tracking-[0.4em] text-xs uppercase">
                <span className="w-12 h-px bg-gold"></span> Our Vision
              </div>
              <h2 className="font-serif text-4xl md:text-5xl leading-tight">
                Bridging Cultures <br />
                <span className="text-gold">and Markets</span>
              </h2>
              <p className="text-platinum/60 leading-relaxed text-lg font-light">
                We envision a world where the timeless elegance of Japanese beauty
                traditions seamlessly integrates with global lifestyles, creating
                meaningful connections across cultures.
              </p>
              <p className="text-platinum/60 leading-relaxed text-lg font-light">
                Our goal is to become the premier gateway for Japanese brands seeking
                authentic global presence, setting new standards for cross-cultural
                brand partnerships.
              </p>
            </div>
            <div className="order-1 lg:order-2 relative group">
              <div className="absolute -inset-4 border border-gold/20 rounded-2xl group-hover:border-gold/40 transition-all duration-700"></div>
              <img
                src="/images/about-craftsmanship.jpg"
                alt="Japanese Craftsmanship"
                className="relative z-10 rounded-xl grayscale group-hover:grayscale-0 transition-all duration-1000 object-cover aspect-[4/5] w-full shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 px-6 lg:px-12 bg-ebony">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-8 border border-white/5 rounded-2xl bg-onyx/30">
              <p className="text-4xl md:text-5xl font-serif text-gold mb-2">20+</p>
              <p className="text-xs uppercase tracking-widest text-platinum/40">
                Years of Expertise
              </p>
            </div>
            <div className="text-center p-8 border border-white/5 rounded-2xl bg-onyx/30">
              <p className="text-4xl md:text-5xl font-serif text-gold mb-2">50+</p>
              <p className="text-xs uppercase tracking-widest text-platinum/40">
                Brand Partnerships
              </p>
            </div>
            <div className="text-center p-8 border border-white/5 rounded-2xl bg-onyx/30">
              <p className="text-4xl md:text-5xl font-serif text-gold mb-2">10+</p>
              <p className="text-xs uppercase tracking-widest text-platinum/40">
                Countries Reached
              </p>
            </div>
            <div className="text-center p-8 border border-white/5 rounded-2xl bg-onyx/30">
              <p className="text-4xl md:text-5xl font-serif text-gold mb-2">100%</p>
              <p className="text-xs uppercase tracking-widest text-platinum/40">
                Client Satisfaction
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 px-6 lg:px-12 bg-onyx">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl mb-4">
              Our Core <span className="text-gold italic">Values</span>
            </h2>
            <div className="w-24 h-px bg-gold mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-10 border border-white/5 rounded-2xl bg-ebony hover:border-gold/30 transition-all duration-500 group">
              <div className="w-16 h-16 rounded-full border border-gold/30 flex items-center justify-center mb-8 group-hover:bg-gold/10 transition-all">
                <span className="text-2xl font-serif text-gold">01</span>
              </div>
              <h3 className="font-serif text-2xl mb-4">Authenticity</h3>
              <p className="text-platinum/50 leading-relaxed">
                We honor the true essence of Japanese craftsmanship and cultural
                heritage in every brand we represent.
              </p>
            </div>
            <div className="p-10 border border-white/5 rounded-2xl bg-ebony hover:border-gold/30 transition-all duration-500 group">
              <div className="w-16 h-16 rounded-full border border-gold/30 flex items-center justify-center mb-8 group-hover:bg-gold/10 transition-all">
                <span className="text-2xl font-serif text-gold">02</span>
              </div>
              <h3 className="font-serif text-2xl mb-4">Excellence</h3>
              <p className="text-platinum/50 leading-relaxed">
                We maintain the highest standards in everything we do, from brand
                selection to market strategy execution.
              </p>
            </div>
            <div className="p-10 border border-white/5 rounded-2xl bg-ebony hover:border-gold/30 transition-all duration-500 group">
              <div className="w-16 h-16 rounded-full border border-gold/30 flex items-center justify-center mb-8 group-hover:bg-gold/10 transition-all">
                <span className="text-2xl font-serif text-gold">03</span>
              </div>
              <h3 className="font-serif text-2xl mb-4">Innovation</h3>
              <p className="text-platinum/50 leading-relaxed">
                We blend traditional wisdom with modern insights to create unique
                market opportunities for our partners.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-6 lg:px-12 bg-ebony">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-4xl md:text-6xl mb-8">
            Embark on a Journey <br />
            <span className="text-gold italic">with Us Today</span>
          </h2>
          <p className="text-platinum/60 text-lg font-light mb-12">
            Let's shape the future of Japanese beauty and lifestyle brands together.
          </p>
          <button
            onClick={emailVerification.handleEmailClick}
            className="bg-gold text-onyx px-12 py-5 rounded-full font-medium tracking-widest uppercase text-xs hover:bg-white transition-all duration-500 inline-block shine-effect"
          >
            Get in Touch
          </button>
        </div>
      </section>

      {/* Email Verification Modal */}
      <EmailVerificationModal {...emailVerification} />
    </div>
  );
};

export default AboutPage;
